import type { WidgetConfigElement } from '~/types/widget';
import type { CatalogMap, ElementSchema } from './catalog';

/**
 * A config element augmented with a transient, editor-only id (`__builderId`) used
 * for selection, drag-and-drop and tree lookups. The id is stripped before the config
 * is persisted or pushed to the live preview (see {@link stripIds}).
 */
export interface BuilderNode extends Omit<WidgetConfigElement, 'children'> {
  __builderId: string;
  children?: BuilderNode[];
}

export interface NodeLocation {
  /** The array (root or a node's children) that directly contains the node. */
  parentArray: BuilderNode[];
  index: number;
  /** The containing node, or null when the node sits at the root. */
  parent: BuilderNode | null;
  node: BuilderNode;
}

let idCounter = 0;

/** Generates a process-unique editor id. Not persisted, so cross-session collisions are irrelevant. */
export function createBuilderId(): string {
  idCounter += 1;

  return `bn_${idCounter}_${Math.random().toString(36).slice(2, 9)}`;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/** Narrowing guard: is this value a cart-widget element tree (vs. a checkout flat record or string)? */
export function isElementTree(config: unknown): config is WidgetConfigElement[] {
  return (
    Array.isArray(config) &&
    config.every(node => !!node && typeof node === 'object' && typeof (node as WidgetConfigElement).type === 'string')
  );
}

function hydrateNode(element: WidgetConfigElement): BuilderNode {
  const { children, ...rest } = element;

  return {
    ...rest,
    __builderId: createBuilderId(),
    attributes: { ...(element.attributes || {}) },
    ...(children ? { children: children.map(hydrateNode) } : {}),
  };
}

/** Deep-clones a config tree and assigns a fresh `__builderId` to every node. */
export function hydrateIds(config: WidgetConfigElement[]): BuilderNode[] {
  return config.map(hydrateNode);
}

/**
 * Parses a stored config value into a builder tree. Returns [] for anything that is not
 * a cart-widget element array (e.g. a checkout flat record or a raw string), and tolerates
 * unknown element `type`s by keeping them as-is (the UI renders them as read-only rows).
 */
export function parseConfigTree(config: unknown): BuilderNode[] {
  if (!isElementTree(config)) return [];

  return hydrateIds(config);
}

function stripNode(node: BuilderNode): WidgetConfigElement {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { __builderId, children, ...rest } = node;

  return {
    ...rest,
    ...(children ? { children: children.map(stripNode) } : {}),
  };
}

/** Deep-clones a builder tree back into a clean config tree, dropping editor ids. */
export function stripIds(nodes: BuilderNode[]): WidgetConfigElement[] {
  return nodes.map(stripNode);
}

function previewNode(node: BuilderNode): WidgetConfigElement {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { __builderId, children, ...rest } = node;

  return {
    ...rest,
    attributes: { ...(node.attributes || {}), 'data-builder-id': __builderId },
    ...(children ? { children: children.map(previewNode) } : {}),
  };
}

/**
 * Produces a preview-only config tree where each element's attributes carry a
 * `data-builder-id="<__builderId>"` mapping back to the editor node. The cart-widget
 * runtime sets attributes on the rendered elements from `attributes`, so the resulting
 * DOM can be queried with `[data-builder-id="X"]` for click-to-select and highlight.
 * NEVER pass this output to save — use {@link stripIds} for persistence.
 */
export function toPreviewConfig(nodes: BuilderNode[]): WidgetConfigElement[] {
  return nodes.map(previewNode);
}

/** Finds a node and its location (parent array + index). Recurses depth-first. */
export function locateNode(nodes: BuilderNode[], id: string, parent: BuilderNode | null = null): NodeLocation | null {
  for (let index = 0; index < nodes.length; index += 1) {
    const node = nodes[index];
    if (node.__builderId === id) return { index, node, parent, parentArray: nodes };

    if (node.children) {
      const found = locateNode(node.children, id, node);
      if (found) return found;
    }
  }

  return null;
}

export function findNode(nodes: BuilderNode[], id: string): BuilderNode | null {
  return locateNode(nodes, id)?.node ?? null;
}

/** True when `candidateId` is `node` itself or one of its descendants (prevents moving a node into itself). */
export function isSelfOrDescendant(node: BuilderNode, candidateId: string): boolean {
  if (node.__builderId === candidateId) return true;

  return (node.children ?? []).some(child => isSelfOrDescendant(child, candidateId));
}

/**
 * In-place removal. Returns the removed node (for re-insertion during a move) or null.
 * Mutates `nodes` so a deep watcher on the tree fires and the preview refreshes.
 */
export function removeNode(nodes: BuilderNode[], id: string): BuilderNode | null {
  const location = locateNode(nodes, id);
  if (!location) return null;

  return location.parentArray.splice(location.index, 1)[0] ?? null;
}

function resolveTargetArray(nodes: BuilderNode[], parentId: string | null): BuilderNode[] | null {
  if (parentId === null) return nodes;

  const location = locateNode(nodes, parentId);
  if (!location) return null;

  if (!location.node.children) location.node.children = [];

  return location.node.children;
}

/** In-place insert of an existing node at `index` under `parentId` (null = root). */
export function insertNode(nodes: BuilderNode[], parentId: string | null, index: number, node: BuilderNode): boolean {
  const target = resolveTargetArray(nodes, parentId);
  if (!target) return false;

  target.splice(clamp(index, 0, target.length), 0, node);

  return true;
}

/**
 * In-place move of a node to a new parent/index. `index` is interpreted against the target
 * array after the node has been removed. Rejects moves into the node's own subtree.
 */
export function moveNode(nodes: BuilderNode[], id: string, targetParentId: string | null, index: number): boolean {
  const location = locateNode(nodes, id);
  if (!location) return false;

  if (targetParentId !== null && isSelfOrDescendant(location.node, targetParentId)) return false;

  const removed = removeNode(nodes, id);
  if (!removed) return false;

  return insertNode(nodes, targetParentId, index, removed);
}

/** In-place attribute update. Replaces the attributes object so even shallow watchers react. */
/** ui-button's click event is chosen by `action`, but only reaches the bus if `emits` names it. */
const BUTTON_ACTION_EVENTS: Record<string, string> = {
  add: 'protection-add',
  remove: 'protection-remove',
};

/** Both types resolve a click from `action` first and `emits` second, so they are kept in step. */
const CLICKABLE_TYPES = ['ui-button', 'custom-text'];

const PROTECTION_EVENTS = ['protection-add', 'protection-remove'];

/** The `action` value that means "navigate", and where it sends a shopper. */
export const BUTTON_ACTION_CART = 'cart';
const CART_PATH = '/cart';

/** Whether a button navigates instead of touching protection. */
function navigates(node: BuilderNode): boolean {
  return node.type === 'ui-button' && String(node.attributes?.href ?? '').trim() !== '';
}

function emitsProtection(node: BuilderNode): boolean {
  return !!node.emits?.some(e => PROTECTION_EVENTS.includes(e));
}

/**
 * Whether a clickable element does nothing when clicked.
 *
 * `emits` is the wiring for both types: BaseComponent registers a bus dispatch per entry, and the
 * click handler fires a NON-bubbling CustomEvent on the element itself, so with no matching entry
 * nothing listens — whatever `action` says. Both `ui-button` and `custom-text` resolve their event
 * from `action` first and `emits` second, so the pair has to be written together.
 *
 * A text with no action at all is a caption, not a broken control, and is never flagged.
 */
export function clickIsInert(node: BuilderNode): 'unwired' | null {
  if (navigates(node)) return null;
  if (node.type === 'ui-button') return emitsProtection(node) ? null : 'unwired';
  if (node.type !== 'custom-text') return null;

  const wantsClick = String(node.attributes?.action ?? '').trim() !== '';

  return wantsClick && !emitsProtection(node) ? 'unwired' : null;
}

export function setNodeAttr(nodes: BuilderNode[], id: string, attr: string, value: string | number | boolean): boolean {
  const node = findNode(nodes, id);
  if (!node) return false;

  node.attributes = { ...node.attributes, [attr]: value };

  // A colour can only reach an SVG's shapes once the file is drawn inline, so choosing one turns
  // that on. Same reasoning as `action`/`emits` below: the dashboard keeps the pair honest rather
  // than making the merchant discover a second switch.
  if (node.type === 'ui-image' && attr === 'color') {
    if (typeof value === 'string' && value.trim()) node.attributes = { ...node.attributes, isSvg: true };
  }

  // A text resolves its click the same way a button does, so both need `emits` written alongside
  // `action` — that is what carries the click to the bus. The runtime deliberately does not fall
  // back to a bus dispatch for a button with no `emits`, because trees saved before `action` was
  // live would start firing; so the dashboard is what has to write it.
  if (CLICKABLE_TYPES.includes(node.type) && attr === 'action') {
    const event = BUTTON_ACTION_EVENTS[String(value)];
    if (event) node.emits = [event];
    else delete node.emits;
  }

  // "Go to Cart" is navigation, and the runtime treats `href` and a protection dispatch as
  // exclusive — leaving the page would abort the cart mutation. So the choice seeds the path, and
  // any other choice clears it, rather than leaving a button that both navigates and mutates.
  if (node.type === 'ui-button' && attr === 'action') {
    node.attributes = { ...node.attributes, href: String(value) === BUTTON_ACTION_CART ? CART_PATH : '' };
  }

  return true;
}

/** Duplicates a node (with fresh ids) and inserts the copy directly after the original. */
export function duplicateNode(nodes: BuilderNode[], id: string): BuilderNode | null {
  const location = locateNode(nodes, id);
  if (!location) return null;

  const clone = hydrateIds(stripIds([location.node]))[0];
  location.parentArray.splice(location.index + 1, 0, clone);

  return clone;
}

/** Validates whether a child of `childType` may be dropped into a parent of `parentType` (null = root). */
export function canDropInto(catalog: CatalogMap, parentType: string | null, childType: string): boolean {
  if (parentType === null) return true;

  const schema = catalog[parentType];
  if (!schema || !schema.canHaveChildren) return false;
  if (!schema.allowedChildTypes) return true;

  return schema.allowedChildTypes.includes(childType);
}

/** Builds a fresh node from a catalog entry, seeding default attributes and children. */
export function buildNodeFromCatalog(schema: ElementSchema): BuilderNode {
  const children = schema.defaultChildren
    ? hydrateIds(schema.defaultChildren)
    : schema.canHaveChildren
      ? []
      : undefined;

  // `emits` is what actually wires a button up: the runtime registers the bus dispatch from it, so
  // a button created without it dispatches a DOM event nobody listens for and does nothing at all.
  // `action` only chooses WHICH event, so the two have to be seeded together.
  const action = schema.defaultAttributes?.action;
  const event = typeof action === 'string' ? BUTTON_ACTION_EVENTS[action] : undefined;

  return {
    __builderId: createBuilderId(),
    attributes: { ...schema.defaultAttributes },
    type: schema.type,
    ...(event ? { emits: [event] } : {}),
    ...(children ? { children } : {}),
  };
}

/**
 * Ensures every container node (whose type is in `containerTypes`) has a `children` array,
 * so drag-and-drop can use it as a drop target even when it starts empty. Mutates in place.
 */
export function normalizeContainers(nodes: BuilderNode[], containerTypes: string[]): void {
  for (const node of nodes) {
    if (containerTypes.includes(node.type) && !node.children) node.children = [];
    if (node.children) normalizeContainers(node.children, containerTypes);
  }
}

/** Short human-readable detail for a node row (e.g. the text content, or an image filename). */
export function getNodeSummary(node: BuilderNode): string {
  const attrs = node.attributes || {};
  const text = attrs.text ?? attrs.modalHeading ?? attrs.title;
  if (typeof text === 'string' && text.trim()) {
    return text.length > 40 ? `${text.slice(0, 40)}…` : text;
  }

  if (typeof attrs.src === 'string' && attrs.src) {
    return attrs.src.split('/').pop() || attrs.src;
  }

  return '';
}
